import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { PortRS485 } from 'z3k-config';
import { unpack_validation } from 'z3k-parser/validation/types';
import { MaterialDropdown, MaterialRadioGroupControl, } from 'console/shared/components/fields';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { SettingCard } from 'console/device-settings/z3k/components/material-utils/settings-card/card';
import { Z3KSettingsEditor } from 'console/device-settings/z3k/provider/types';
import { Control } from 'console/shared/components/fields/control';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { SettingFullRow } from 'z3k-settings/components/material-utils/settings-card';
import { Alert } from '@mui/material';
const port_function_values = [
    {
        label: 'Обычный режим',
        value: PortRS485.Type.standard.toString(),
        help_id: 'modbus_setting_register',
    },
    {
        label: 'Режим Modbus',
        value: PortRS485.Type.modbus.toString(),
        help_id: 'modbus_setting_register',
    },
];
const stop_byte_values = [
    {
        label: '1',
        value: PortRS485.stopByteCounts[0].toString(),
        help_id: 'modbus_stop_byte_count',
    },
    {
        label: '2',
        value: PortRS485.stopByteCounts[1].toString(),
        help_id: 'modbus_stop_byte_count',
    },
];
const speed_options = [
    { text: 'Не выбрана', value: 0 },
    ...PortRS485.speeds.map((speed) => ({
        text: speed.toString(),
        value: speed,
    })),
];
export const ModBusSettings = memo(function ModBusSettings() {
    const { config, validation_result, validation_warnings_result, replace_config } = useContext(Z3KSettingsEditor);
    const _validation_result = useMemo(() => unpack_validation(validation_result, 'port_rs485'), [validation_result]);
    const _validation_warnings_result = useMemo(() => unpack_validation(validation_warnings_result, 'port_rs485'), [validation_warnings_result]);
    const port_type_field = useCallback((input_props) => (_jsx(MaterialRadioGroupControl, { control_values: port_function_values, ...input_props })), [port_function_values]);
    const port_type = useMemo(() => config.port_rs485?.setting_register === PortRS485.Type.modbus ? '1' : '0', [config.port_rs485]);
    const onChangePortType = useCallback((value) => {
        replace_config({
            ...config,
            //@ts-ignore
            port_rs485: {
                ...config.port_rs485,
                setting_register: value === '1' ? PortRS485.Type.modbus : PortRS485.Type.standard,
            },
        });
    }, [config]);
    const stop_byte_field = useCallback((input_props) => (_jsx(MaterialRadioGroupControl, { control_values: stop_byte_values, ...input_props })), [stop_byte_values]);
    const stop_byte = useMemo(() => config.port_rs485?.stop_byte_count === PortRS485.stopByteCounts[1]
        ? '2'
        : '1', [config.port_rs485]);
    const onChangeStopByte = useCallback((value) => {
        replace_config({
            ...config,
            //@ts-ignore
            port_rs485: {
                ...config.port_rs485,
                stop_byte_count: value === '1'
                    ? PortRS485.stopByteCounts[0]
                    : PortRS485.stopByteCounts[1],
            },
        });
    }, [config]);
    const speed_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: speed_options })), [speed_options]);
    const onChangeSpeed = useCallback((value) => {
        replace_config({
            ...config,
            //@ts-ignore
            port_rs485: {
                ...config.port_rs485,
                speed: value,
            },
        });
    }, [config]);
    const parity_check_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: PortRS485.parityCheckDict })), [PortRS485.parityCheckDict]);
    const onChangeParityCheck = useCallback((value) => {
        replace_config({
            ...config,
            //@ts-ignore
            port_rs485: {
                ...config.port_rs485,
                parity_check: value,
            },
        });
    }, [config]);
    const canShowHelpV2Devices = useMemo(() => {
        const isModbusActive = config.port_rs485?.setting_register === PortRS485.Type.modbus;
        const isH1000V2 = Number(config.device.hardware) === 722 &&
            config.device.name === 'H1000_PRO';
        const isH2000V2 = Number(config.device.hardware) === 723 &&
            config.device.name === 'H2000_PRO';
        return isModbusActive && (isH1000V2 || isH2000V2);
    }, [config.port_rs485, config.device]);
    return (_jsx(ErrorBoundary, { children: _jsxs(SettingCard, { title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0430 \u043F\u043E\u0440\u0442\u0430 RS485", children: [canShowHelpV2Devices && (_jsx(SettingFullRow, { children: _jsxs(Alert, { severity: "warning", sx: { width: '100%' }, children: ["\u0414\u043B\u044F \u044D\u0442\u043E\u0433\u043E \u0443\u0441\u0442\u0440\u043E\u0439\u0441\u0442\u0432\u0430, \u043F\u043E\u0434 modbus \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u0435\u0442\u0441\u044F", ' ', _jsx("b", { children: "\u0442\u043E\u043B\u044C\u043A\u043E \u043D\u0438\u0436\u043D\u0438\u0439 \u043F\u043E\u0440\u0442" }), " ."] }) })), _jsx(ControlWithoutValidation, { label: "\u0424\u0443\u043D\u043A\u0446\u0438\u044F \u043F\u043E\u0440\u0442\u0430", value: port_type, onChange: onChangePortType, field: port_type_field }), _jsx(ControlWithoutValidation, { label: "\u041A\u043E\u043B\u0438\u0447\u0435\u0441\u0442\u0432\u043E \u0441\u0442\u043E\u043F-\u0431\u0438\u0442\u043E\u0432", value: stop_byte, onChange: onChangeStopByte, field: stop_byte_field }), _jsx(Control, { label: "\u0421\u043A\u043E\u0440\u043E\u0441\u0442\u044C \u043F\u0435\u0440\u0435\u0434\u0430\u0447\u0438", value: config.port_rs485?.speed ?? PortRS485.speeds[2], onChange: onChangeSpeed, validation_result: unpack_validation(_validation_result, 'speed'), validation_warnings_result: unpack_validation(_validation_warnings_result, 'speed'), field: speed_field, help_id: "modbus_speed" }), _jsx(Control, { label: "\u041A\u043E\u043D\u0442\u0440\u043E\u043B\u044C \u043F\u043E \u0447\u0435\u0442\u043D\u043E\u0441\u0442\u0438", value: config.port_rs485?.parity_check ??
                        PortRS485.parityCheckDict[0].value, onChange: onChangeParityCheck, validation_result: unpack_validation(_validation_result, 'parity_check'), validation_warnings_result: unpack_validation(_validation_warnings_result, 'parity_check'), field: parity_check_field, help_id: "modbus_parity_check" })] }) }));
});
